import { useEffect, useState } from 'react'
import { Tooltip } from 'antd'
import { useRecoilState, useRecoilValue } from 'recoil'
import { listPointState } from 'store/buildingStore'
import currentViewerState from 'store/viewerStore'

import LazyThumbnail, { ImageType } from 'components/atoms/LazyThumbnail'

import { SubImageOverlay } from '../type'

export default function SubImageThubnailCarousel({
  subImageOverlay,
  handleClickSubImage,
  subImageCarousel,
  matches
}: {
  subImageOverlay: SubImageOverlay
  handleClickSubImage: (subImageId: number) => void
  subImageCarousel: any
  matches: boolean
}) {
  const points = useRecoilValue(listPointState)
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const [overlayLeft, setOverlayLeft] = useState(0)
  const [overlayWidth, setOverlayWidth] = useState(0)

  const calculatePosition = () => {
    const screenWidth = window.innerWidth

    let responsiveMaxWidth = 900
    let responsiveRight = 180
    let responsiveLeft = 0
    if (screenWidth < 500) {
      responsiveMaxWidth = 320
      responsiveRight = 20
      responsiveLeft = 20
    } else if (screenWidth < 768) {
      responsiveMaxWidth = 480
      responsiveRight = 180
      responsiveLeft = 55
    } else if (screenWidth < 1024) {
      responsiveMaxWidth = 700
      responsiveRight = 120
      responsiveLeft = 40
    } else if (screenWidth > 1024) {
      responsiveLeft = 45
    }

    const point = points.find((p) => p.id === subImageOverlay.pointId)
    if (!point) return

    let { subImages } = point
    if (
      (!subImages || subImages.length === 0) &&
      currentViewer.pointDetail?.id === point.id
    ) {
      subImages = currentViewer.pointDetail.subImages
    }
    const subImageCount = subImages?.length || 0
    const imageItemWidth = matches ? 168 : 120 // size of image item
    const imageGap = matches ? 20 : 10 // gap between image items
    const totalGap = Math.max(0, subImageCount - 1) * imageGap + 25
    const totalImageWidth = subImageCount * imageItemWidth + totalGap

    // Clamp overlay width so it shows all thumbnails but respects responsive limits
    const overlayWidth = Math.min(
      responsiveMaxWidth,
      Math.max(matches ? 200 : 150, totalImageWidth)
    )

    let { left } = subImageOverlay

    if (screenWidth < 500) {
      // New logic for mobile screens
      const minLeft = 10
      const maxLeft = screenWidth - overlayWidth - 10
      const isNearLeftEdge = left < screenWidth * 0.15
      const isNearRightEdge = left > screenWidth * 0.85 - subImageOverlay.width

      if (isNearLeftEdge) {
        left = 10
      } else if (isNearRightEdge) {
        left = screenWidth - overlayWidth - 10
      } else {
        left = left + subImageOverlay.width / 2 - overlayWidth / 2
        left = Math.max(minLeft, Math.min(left, maxLeft))
      }
    } else if (screenWidth > 768 && screenWidth < 1024) {
      // New logic for mobile screens
      const minLeft = 10
      const maxLeft = screenWidth - overlayWidth - 10
      const isNearLeftEdge = left < screenWidth * 0.15
      const isNearRightEdge = left > screenWidth * 0.85 - subImageOverlay.width

      if (isNearLeftEdge) {
        left = 10
      } else if (isNearRightEdge) {
        left = screenWidth - overlayWidth - 10
      } else {
        left = left + subImageOverlay.width / 2 - overlayWidth / 2
        left = Math.max(minLeft, Math.min(left, maxLeft))
      }
    } else {
      // Original logic for desktop screens
      const minLeft = responsiveLeft
      const maxLeft = screenWidth - overlayWidth - 10

      if (left < minLeft) {
        left = subImageOverlay.left + subImageOverlay.width / 4 - responsiveLeft
      } else if (left > maxLeft) {
        left = subImageOverlay.left - overlayWidth + responsiveRight
      }
    }

    setOverlayWidth(overlayWidth)
    setOverlayLeft(left)
  }

  useEffect(() => {
    calculatePosition()
    window.addEventListener('resize', calculatePosition)
    return () => {
      window.removeEventListener('resize', calculatePosition)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subImageOverlay, points, currentViewer])

  if (!subImageOverlay.isOpen) return null

  const point = points.find((p) => p.id === subImageOverlay.pointId)

  if (!point) return null

  let { subImages } = point
  if (
    (!subImages || subImages.length === 0) &&
    currentViewer.pointDetail?.id === point.id
  ) {
    subImages = [...(currentViewer?.pointDetail?.subImages || [])]?.sort(
      (a, b) => a.id - b.id
    )
  }

  return (
    <div
      id="subimage-overlay"
      style={{
        position: 'fixed',
        bottom: matches ? '168px' : '142px',
        left: overlayLeft,
        width: overlayWidth,
        zIndex: 99999,
        pointerEvents: 'auto'
      }}
      className={`bg-white/30 backdrop-blur-[2px] ${
        matches ? 'p-3' : 'p-2'
      } border border-gray-200 transition-all duration-300 ease-out opacity-0 scale-95 animate-subimage-fadein ${
        subImageOverlay.isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
      }`}
    >
      <div
        ref={subImageCarousel.containerRef}
        className={`flex gap-2 overflow-x-auto no-scrollbar shadow-2xl transition-all duration-500 ease-in-out ${
          subImages && subImages?.length < 5
            ? 'justify-center'
            : 'justify-start'
        }`}
        style={{
          cursor: subImageCarousel.isDragging ? 'grabbing' : 'grab',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          userSelect: 'none',
          width: '100%',
          maxWidth: '900px'
        }}
        onMouseDown={subImageCarousel.onMouseDown}
        onMouseMove={subImageCarousel.onMouseMove}
        onMouseUp={subImageCarousel.onMouseUp}
        onMouseLeave={subImageCarousel.onMouseLeave}
        onTouchStart={subImageCarousel.onTouchStart}
        onTouchMove={subImageCarousel.onTouchMove}
        onTouchEnd={subImageCarousel.onTouchEnd}
        onMouseEnter={subImageCarousel.onMouseEnter}
      >
        {subImages?.map((subImage) => {
          const isSelected = subImage.id === currentViewer.selectedImage
          return (
            <div
              key={subImage.id}
              className={`relative group flex-shrink-0 ${
                isSelected
                  ? 'border-[3px] border-white border-solid'
                  : 'border-0'
              } ${
                currentViewer.imageIsLoading && !isSelected
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer'
              }`}
              style={{ minWidth: matches ? '168px' : '140px' }}
              onClick={() => {
                if (currentViewer.imageIsLoading) return

                setCurrentViewer({
                  ...currentViewer,
                  selectedImage:
                    subImage.id !== currentViewer.selectedImage
                      ? subImage.id
                      : currentViewer.pointDetail?.image360?.id
                })

                subImage.id !== currentViewer.selectedImage &&
                  handleClickSubImage(subImage.id)
              }}
            >
              <div
                className="relative overflow-hidden"
                style={{
                  width: matches ? '168px' : '140px',
                  height: matches ? '117px' : '90px'
                }}
              >
                <LazyThumbnail
                  imageType={ImageType.OBJECT_FILL}
                  url={
                    subImage.multires
                      ? `${subImage.tilesUrl}thumb.jpg`
                      : subImage.thumbnail || ''
                  }
                  className="w-full h-full"
                  name={subImage.name || ''}
                  width={matches ? 168 : 90}
                  height={matches ? 117 : 110}
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 transition-all duration-200 rounded-md" />
                <Tooltip
                  title={subImage.name}
                  placement="top"
                  color="#0E0D0A80"
                  trigger="hover"
                >
                  <div className="absolute bottom-0 select-none left-0 px-2 right-0 bg-black bg-opacity-50 text-white text-[13px] font-bold p-1 text-center overflow-ellipsis whitespace-nowrap overflow-hidden">
                    {subImage.name}
                  </div>
                </Tooltip>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
